import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import "./Withdrawal.css";
import InnerHeader from "../../components/InnerHeader";
import useTranslate from "../../hooks/useTranslate";

export default function Withdrawal({
  items = [],
  general,
  userData,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslate();

  const token = localStorage.getItem("token");
  const remoteId = localStorage.getItem("remote_id");

  const amounts = ["2000", "3000", "5000", "10000", "15000", "20000", "25000", "1000", "500"];

  const [isBankMenuOpen, setIsBankMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [activeAmount, setActiveAmount] = useState(
    location.state?.amount ? String(location.state.amount) : ""
  );
  const [icon, setIcon] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [pgs, setPgs] = useState([]);
  const [name, setName] = useState("");
  const [accountName, setAccountName] = useState(userData?.name || "");

  useEffect(() => {
    setAccountName(userData?.name || "");
  }, [userData]);

  useEffect(() => {
    const uniquePaymentMethods = [];
    const seenNames = new Set();

    items.forEach((item) => {
      const key = item?.name?.trim()?.toLowerCase();
      if (key && !seenNames.has(key)) {
        seenNames.add(key);
        uniquePaymentMethods.push(item);
      }
    });

    if (Number(general?.withdrawal_status) === 0) {
      setPgs([]);
      return;
    }

    setPgs(uniquePaymentMethods);
  }, [items, general]);

  useEffect(() => {
    if (!pgs.length) return;

    const firstItem = pgs.find((item) => item.status === "active") || pgs[0];

    setActiveItem(firstItem.id);
    setName(firstItem.name);
    setIcon(firstItem.icon || "");
  }, [pgs]);

  useEffect(() => {
    const fetchOtherContracts = async () => {
      if (!token) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/get/other/phone`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          console.error("Failed to fetch contracts");
          return;
        }

        const data = await response.json();
        const verifiedNumbers = [];

        if (String(userData?.session_id) === "1" && userData?.phone) {
          verifiedNumbers.push(userData.phone);
        }

        if (Array.isArray(data?.data)) {
          data.data.forEach((item) => {
            if (item?.isverify && item?.contract_value) {
              verifiedNumbers.push(item.contract_value);
            }
          });
        }

        setNumbers(verifiedNumbers);

        if (!selectedAccount && verifiedNumbers.length > 0) {
          setSelectedAccount(verifiedNumbers[0]);
        }
      } catch (error) {
        console.error("Error fetching contracts:", error);
      }
    };

    fetchOtherContracts();
  }, [token, userData, selectedAccount]);

  const checkUserEligibility = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/userwithdrawcheck?remote_id=${remoteId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 400) {
          if (data?.error && /Date of Birth/i.test(data.error)) {
            setModalMessage(`<a href="/personal">${data.error}</a>`);
            navigate('/personal')
          } else {
            setModalMessage(
              data?.error ||
                `<a href="/turnover-record">${t("pleaseCompleteTurnover")}</a>`
            );
          }
        } else {
          setModalMessage(data?.error || t("anErrorOccurred"));
        }

        setShowModal(true);
        return false;
      }

      if (data?.success) return true;

      setModalMessage(t("anUnknownErrorOccurred"));
      setShowModal(true);
      return false;
    } catch (error) {
      setModalMessage(t("eligibilityCheckFailed"));
      setShowModal(true);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !activeItem) {
      setModalMessage(t("pleaseSelectBank"));
      setShowModal(true);
      return;
    }

    if (!accountName.trim()) {
      setModalMessage(t("accountNameRequired"));
      setShowModal(true);
      return;
    }

    if (!selectedAccount.trim()) {
      setModalMessage(t("bankNumberRequired"));
      setShowModal(true);
      return;
    }

    if (!activeAmount) {
      setModalMessage(t("amountRequired"));
      setShowModal(true);
      return;
    }

    const eligible = await checkUserEligibility();
    if (!eligible) return;

    setSubmitLoading(true);

    const requestBody = {
      remote_id: remoteId,
      account_name: accountName.trim(),
      account_number: selectedAccount.trim(),
      amount: activeAmount,
      payment_id: activeItem,
      provider: name,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/submit-withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setModalMessage(data?.error || t("failedToSubmitWithdrawal"));
        setShowModal(true);
        return;
      }

      if (data?.success) {
        setModalMessage(t("withdrawalSuccessful"));
        setShowModal(true);

        setTimeout(() => {
          navigate("/payment-request", {
            state: {
              amount: activeAmount,
              payment_icon: icon,
            },
          });
        }, 700);
      } else {
        setModalMessage(data?.message || t("somethingWentWrong"));
        setShowModal(true);
      }
    } catch (error) {
      setModalMessage(t("anErrorOccurredPleaseTryAgain"));
      setShowModal(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="withdrawal-page">
      <InnerHeader title={t("withdrawal")} />

      <div className="withdrawal-content">
        <p className="withdrawal-note">
          {t("pleaseBindBankAccountBeforeWithdrawMoney")}
        </p>

        <form className="withdrawal-form" onSubmit={handleSubmit}>
          <div className="withdrawal-row">
            <label className="withdrawal-label">{t("bank")}</label>

            <div className="withdrawal-field-wrap">
              <button
                type="button"
                className="withdrawal-select"
                onClick={() => setIsBankMenuOpen((prev) => !prev)}
                disabled={!pgs.length}
              >
                <span>{name || t("selectBank")}</span>
                <ChevronDown
                  size={20}
                  className={isBankMenuOpen ? "rotate-up" : ""}
                />
              </button>

              {isBankMenuOpen && pgs.length > 0 && (
                <div className="withdrawal-dropdown">
                  {pgs.map((item) => {
                    const isActive = item.id === activeItem;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`withdrawal-option ${isActive ? "active" : ""}`}
                        onClick={() => {
                          setActiveItem(item.id);
                          setName(item.name);
                          setIcon(item.icon || "");
                          setIsBankMenuOpen(false);
                        }}
                      >
                        <span className="withdrawal-check">
                          {isActive ? <Check size={18} /> : null}
                        </span>
                        <span>{item.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="withdrawal-row">
            <label className="withdrawal-label">{t("accountName")}</label>
            <div className="withdrawal-field-wrap">
              <input
                type="text"
                className="withdrawal-input"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={t("enterAccountName")}
              />
            </div>
          </div>

          <div className="withdrawal-row">
            <label className="withdrawal-label">{t("bankNo")}</label>
            <div className="withdrawal-field-wrap">
              <input
                type="text"
                className="withdrawal-input"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                placeholder={t("enterBankNumber")}
              />

              {numbers.length > 1 && (
                <div className="withdrawal-saved-list">
                  {numbers.map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={`withdrawal-saved-chip ${
                        selectedAccount === num ? "active" : ""
                      }`}
                      onClick={() => setSelectedAccount(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!location.state?.amount && (
            <div className="withdrawal-amount-block">
              <label className="withdrawal-amount-label">{t("amount")}</label>
              <div className="withdrawal-amount-grid">
                {amounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={`withdrawal-amount-chip ${
                      activeAmount === amount ? "active" : ""
                    }`}
                    onClick={() => setActiveAmount(amount)}
                  >
                    {amount}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="withdrawal-submit-btn"
            disabled={submitLoading}
          >
            {submitLoading ? t("submitting") : t("submit")}
          </button>
        </form>
      </div>

      {showModal && (
        <div
          className="withdrawal-modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="withdrawal-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="withdrawal-modal-text"
              dangerouslySetInnerHTML={{ __html: modalMessage }}
            />
            <button
              type="button"
              className="withdrawal-modal-btn"
              onClick={() => setShowModal(false)}
            >
              {t("ok")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}